'use client';

import React, { useState, useCallback, useEffect, useRef, useId } from 'react';
import Link from 'next/link';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
}

export default function PricingCalculator() {
  const [emdAmount, setEmdAmount] = useState(250000);
  const [dueDiligenceDays, setDueDiligenceDays] = useState(30);
  // Debounced announcement value — updated 600 ms after the user stops dragging
  const [announcedTotal, setAnnouncedTotal] = useState<string | null>(null);
  const announceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable IDs for label↔input associations
  const emdSliderId = useId();
  const daysSliderId = useId();
  const disclaimerId = useId();

  const ADMIN_FEE = 2500;
  const RATE_PER_DAY_PER_1K = 1; // internal — not exposed in any user-facing string

  const dealFee = emdAmount / 1000 * RATE_PER_DAY_PER_1K * dueDiligenceDays;
  const totalCost = ADMIN_FEE + dealFee;
  const capitalKeptWorking = emdAmount - totalCost;
  const capitalPct = Math.max(0, Math.min(100, capitalKeptWorking / emdAmount * 100));
  const costPct = 100 - capitalPct;

  // Debounce live-region announcement so screen readers aren't flooded while dragging
  const scheduleAnnouncement = useCallback((total: number) => {
    if (announceTimerRef.current) clearTimeout(announceTimerRef.current);
    announceTimerRef.current = setTimeout(() => {
      setAnnouncedTotal(formatCurrency(total));
    }, 600);
  }, []);

  const handleEmdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setEmdAmount(val);
    const fee = val / 1000 * RATE_PER_DAY_PER_1K * dueDiligenceDays;
    scheduleAnnouncement(ADMIN_FEE + fee);
  }, [dueDiligenceDays, scheduleAnnouncement]);

  const handleDaysChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setDueDiligenceDays(val);
    const fee = emdAmount / 1000 * RATE_PER_DAY_PER_1K * val;
    scheduleAnnouncement(ADMIN_FEE + fee);
  }, [emdAmount, scheduleAnnouncement]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (announceTimerRef.current) clearTimeout(announceTimerRef.current);
    };
  }, []);

  return (
    <section className="px-6 pb-10" id="pricing-calculator" aria-label="Pricing calculator" aria-describedby={disclaimerId}>
      {/* Polite live region — announces updated total ~600 ms after slider settles */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only">
        {announcedTotal ? `Estimated total cost updated to ${announcedTotal}` : ''}
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Section label */}
        <div className="mb-8 px-2 py-6">
          <div className="relative">
            <span
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.28em] px-3 py-1.5 rounded-full mb-4"
              style={{ background: 'rgba(82,183,136,0.12)', color: 'var(--color-primary)' }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: 'var(--color-accent)' }} />
              Interactive Calculator
            </span>
            <h2
              className="font-display"
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
                color: 'var(--color-foreground)',
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
                fontWeight: 700
              }}>
              Earnest Bridge Keeps Your{' '}
              <span style={{ color: 'var(--color-green-text)' }}>Capital Working</span>
            </h2>
            <p className="mt-2 text-sm max-w-lg" style={{ color: 'var(--color-muted)', lineHeight: 1.6 }}>Adjust the sliders below to see example fee structure.</p>
          </div>
        </div>

        {/* Calculator card */}
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: 'var(--color-primary-dark)',
            border: '1px solid rgba(82,183,136,0.15)'
          }}>
          <div className="grid grid-cols-1 lg:grid-cols-2">

            {/* ── Left Panel: Inputs ── */}
            <div
              className="p-8 md:p-10 flex flex-col gap-8"
              style={{ borderRight: '1px solid rgba(82,183,136,0.1)' }}>

              {/* EMD Amount */}
              <div>
                {/* Explicit label associated via htmlFor */}
                <label
                  htmlFor={emdSliderId}
                  className="block text-[10px] font-bold uppercase tracking-[0.28em] mb-4"
                  style={{ color: 'var(--color-accent)' }}>
                  1. EMD Amount
                </label>
                <p
                  className="font-display leading-none mb-1"
                  aria-hidden="true"
                  style={{
                    fontSize: 'clamp(2.8rem, 6vw, 4rem)',
                    color: 'white',
                    letterSpacing: '-0.04em'
                  }}>
                  {formatCurrency(emdAmount)}
                </p>
                <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.55)' }}>Drag slider to adjust EMD amount</p>

                {/* Slider — input height is 44px so the native hit area is 44×44px */}
                <div className="relative">
                  <input
                    type="range"
                    id={emdSliderId}
                    min={25000}
                    max={1000000}
                    step={5000}
                    value={emdAmount}
                    onChange={handleEmdChange}
                    className="calc-slider w-full"
                    aria-valuemin={25000}
                    aria-valuemax={1000000}
                    aria-valuenow={emdAmount}
                    aria-valuetext={formatCurrency(emdAmount)}
                  />
                  <div className="flex justify-between mt-2" aria-hidden="true">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>$25,000</span>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>$1,000,000</span>
                  </div>
                </div>
              </div>

              {/* Due Diligence Period */}
              <div>
                <label
                  htmlFor={daysSliderId}
                  className="block text-[10px] font-bold uppercase tracking-[0.28em] mb-4"
                  style={{ color: 'var(--color-accent)' }}>
                  2. Due Diligence Period
                </label>
                <p
                  className="font-display leading-none mb-1"
                  aria-hidden="true"
                  style={{
                    fontSize: 'clamp(2.8rem, 6vw, 4rem)',
                    color: 'white',
                    letterSpacing: '-0.04em'
                  }}>
                  {dueDiligenceDays} <span className="text-2xl font-sans font-light" style={{ color: 'rgba(255,255,255,0.5)' }}>days</span>
                </p>
                <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.55)' }}>Drag slider to adjust due diligence period</p>

                {/* Slider */}
                <div className="relative">
                  <input
                    type="range"
                    id={daysSliderId}
                    min={7}
                    max={90}
                    step={1}
                    value={dueDiligenceDays}
                    onChange={handleDaysChange}
                    className="calc-slider w-full"
                    aria-valuemin={7}
                    aria-valuemax={90}
                    aria-valuenow={dueDiligenceDays}
                    aria-valuetext={`${dueDiligenceDays} days`}
                  />
                  <div className="flex justify-between mt-2" aria-hidden="true">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>7 days</span>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>90 days</span>
                  </div>
                </div>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-4 pt-2" aria-hidden="true">
                {['100% EMD Funded', 'No Capital Required', 'Close More Deals'].map((badge) =>
                  <div key={badge} className="flex items-center gap-1.5">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
                      <circle cx="7" cy="7" r="6.5" stroke="rgba(82,183,136,0.6)" />
                      <path d="M4.5 7L6.5 9L9.5 5.5" stroke="#52B788" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>{badge}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Right Panel: Results ── */}
            <div className="p-8 md:p-10 flex flex-col gap-6">

              {/* Capital You Keep Working */}
              <div>
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.28em] mb-3"
                  style={{ color: 'rgba(255,255,255,0.55)' }}>
                  Capital You Keep Working
                </p>
                <p
                  className="font-display leading-none"
                  style={{
                    fontSize: 'clamp(2.6rem, 5vw, 3.8rem)',
                    color: 'var(--color-accent-light)',
                    letterSpacing: '-0.04em'
                  }}>
                  {capitalKeptWorking > 0 ? formatCurrency(capitalKeptWorking) : '$0'}
                </p>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  Available for your next acquisition
                </p>
              </div>

              {/* Capital vs Cost bar */}
              <div aria-hidden="true">
                <div
                  className="rounded-xl overflow-hidden flex h-12"
                  style={{ background: 'rgba(255,255,255,0.06)' }}>
                  {/* Capital portion */}
                  <div
                    className="flex items-center justify-center transition-all duration-500"
                    style={{
                      width: `${capitalPct}%`,
                      background: 'rgba(82,183,136,0.25)',
                      borderRight: '1px solid rgba(82,183,136,0.3)'
                    }}>
                    {capitalPct > 20 &&
                      <div className="text-center px-2">
                        <p className="text-xs font-bold" style={{ color: 'white' }}>
                          {capitalPct.toFixed(1)}%
                        </p>
                        <p className="text-[9px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.7)' }}>
                          Your Capital
                        </p>
                      </div>
                    }
                  </div>
                  {/* Cost portion */}
                  <div
                    className="flex items-center justify-center transition-all duration-500"
                    style={{
                      width: `${costPct}%`,
                      background: 'rgba(201,168,76,0.15)'
                    }}>
                    {costPct > 8 &&
                      <div className="text-center px-2">
                        <p className="text-xs font-bold" style={{ color: 'var(--color-gold)' }}>
                          {costPct.toFixed(1)}%
                        </p>
                        <p className="text-[9px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.7)' }}>
                          Cost to Secure
                        </p>
                      </div>
                    }
                  </div>
                </div>
              </div>

              {/* Fee Breakdown — semantic output + definition list */}
              <div
                className="rounded-2xl overflow-hidden"
                style={{ border: '1px solid rgba(82,183,136,0.15)' }}>
                <div
                  className="w-full flex items-center px-5 py-4"
                  style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <span
                    className="text-[11px] font-bold uppercase tracking-[0.22em]"
                    style={{ color: 'rgba(255,255,255,0.7)' }}>
                    Fee Breakdown
                  </span>
                </div>

                {/* Use <output> to semantically mark the calculated result region */}
                <output
                  aria-label="Estimated Deal Fee breakdown"
                  className="block px-5 pb-5 pt-1"
                  style={{ borderTop: '1px solid rgba(82,183,136,0.1)' }}>
                  {/* Definition list expresses term/value relationships without altering layout */}
                  <dl className="flex flex-col gap-0">
                    {[
                      {
                        label: 'Administrative Fee',
                        value: formatCurrency(ADMIN_FEE),
                        note: 'Flat fee · per deal',
                        bold: false
                      },
                      {
                        label: `Estimated Deal Fee (${dueDiligenceDays} days)`,
                        value: formatCurrency(dealFee),
                        note: 'Based on EMD amount and due-diligence period',
                        bold: false
                      },
                      {
                        label: 'Total Cost',
                        value: formatCurrency(totalCost),
                        note: 'All-in · no hidden fees',
                        bold: true
                      }
                    ].map((row, i, arr) =>
                      <div
                        key={row.label}
                        className="flex items-center justify-between py-3"
                        style={{
                          borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none'
                        }}>
                        <dt
                          className={`text-sm ${row.bold ? 'font-bold' : 'font-medium'}`}
                          style={{ color: row.bold ? 'white' : 'rgba(255,255,255,0.7)' }}>
                          {row.label}
                          <span className="block text-[11px] mt-0.5 font-normal" style={{ color: 'rgba(255,255,255,0.55)' }}>
                            {row.note}
                          </span>
                        </dt>
                        <dd
                          className={`font-display ${row.bold ? 'text-xl' : 'text-lg'}`}
                          style={{
                            color: row.bold ? 'var(--color-gold)' : 'rgba(255,255,255,0.8)',
                            letterSpacing: '-0.02em'
                          }}>
                          {row.value}
                        </dd>
                      </div>
                    )}
                  </dl>
                </output>
              </div>

              {/* CTA */}
              <Link
                href="/contact"
                className="flex items-center justify-center gap-2 rounded-[6px] font-bold text-sm uppercase tracking-[0.15em] py-4 px-8 transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5"
                style={{ background: 'var(--color-accent)', color: '#0D1F17' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                  <path d="M8 2L8 14M2 8L14 8" stroke="#0D1F17" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Apply for EMD Funding
              </Link>

              {/* Disclaimer — programmatically associated with the section via aria-describedby on <section> */}
              <p
                id={disclaimerId}
                className="text-[11px] leading-relaxed text-center mt-2"
                style={{ color: 'rgba(255,255,255,0.55)' }}>
                Calculator results are estimates for planning purposes. Actual Deal Fees may vary based on deal size, due-diligence timeline, industry, and transaction structure. Final pricing is disclosed upfront before acceptance into the EMD Program.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Slider styles */}
      <style>{`
        /* The wrapper padding workaround from Phase 5 did NOT expand the native
           <input type="range"> hit area — wrapper div padding is non-interactive.
           The fix is to set height directly on the <input> itself to ≥ 44px so
           the input's own bounding box satisfies WCAG 2.2 SC 2.5.8 (min 24×24px).
           The 4px visual track is maintained via the pseudo-element height. */
        .calc-slider-wrapper {
          /* Wrapper padding removed — it served no accessibility purpose because
             clicking the padding area of a wrapper div does not activate the
             native range input inside it. No layout side-effects to remove. */
        }

        .calc-slider {
          -webkit-appearance: none;
          appearance: none;
          /* height set to 44px on the input element itself — this is the actual
             rendered bounding box / hit area of the control.
             Satisfies WCAG 2.2 SC 2.5.8: 44px ≥ 24px minimum. */
          height: 44px;
          border-radius: 2px;
          background: transparent;
          /* outline: none removed — replaced with accessible focus ring below */
          cursor: pointer;
          display: block;
        }

        /* Accessible focus ring on dark background — matches Phase 4 dark-bg focus system */
        .calc-slider:focus-visible {
          outline: 3px solid #52B788;
          outline-offset: 4px;
          border-radius: 4px;
        }

        .calc-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: var(--color-accent);
          border: 3px solid var(--color-primary-dark);
          box-shadow: 0 0 0 2px var(--color-accent);
          cursor: pointer;
          transition: box-shadow 0.2s;
          /* Center thumb vertically within the 44px input height */
          margin-top: -9px;
        }
        .calc-slider::-webkit-slider-thumb:hover {
          box-shadow: 0 0 0 5px rgba(82,183,136,0.25);
        }
        .calc-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: var(--color-accent);
          border: 3px solid var(--color-primary-dark);
          box-shadow: 0 0 0 2px var(--color-accent);
          cursor: pointer;
        }
        .calc-slider::-webkit-slider-runnable-track {
          height: 4px;
          border-radius: 2px;
          background: rgba(255,255,255,0.12);
        }
        .calc-slider::-moz-range-track {
          height: 4px;
          border-radius: 2px;
          background: rgba(255,255,255,0.12);
        }

        /* Screen-reader-only utility (in case Tailwind sr-only is not available in this scope) */
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0,0,0,0);
          white-space: nowrap;
          border-width: 0;
        }
      `}</style>
    </section>
  );
}